import type { toast as sonnerToast } from 'sonner';

export type Toast = typeof sonnerToast;

// sonner loads with the toaster after first paint (see AppToaster), and its Toaster only renders
// toasts created after it subscribes. Every toast goes through `withToaster` so one sent while that
// chunk is still loading is held instead of silently dropped.
let loadedToast: Toast | null = null;
let mountedShells = 0;
let pending: Array<(toast: Toast) => void> = [];

/**
 * Sends a toast. Once sonner has loaded this calls `show` right away — exactly as importing
 * `toast` would, including dropping it on a route with no AppToaster. While the toaster is still
 * loading, `show` waits for it only if an AppToaster is mounted to load it; with none on the page
 * the toast is dropped, as it always was, rather than surfacing later on another page.
 *
 * Returns a function that cancels a toast still waiting (a no-op once it was sent).
 */
export function withToaster(show: (toast: Toast) => void): () => void {
    if (loadedToast) {
        show(loadedToast);
        return () => {};
    }
    if (mountedShells === 0) return () => {};
    pending.push(show);
    return () => {
        pending = pending.filter((queued) => queued !== show);
    };
}

/**
 * Called by each AppToaster as it mounts (in a layout effect, so before any passive effect in the
 * same commit can toast). Returns the unmount cleanup; when the last one goes away before sonner
 * loaded, waiting toasts are dropped with it.
 */
export function appToasterMounted(): () => void {
    mountedShells += 1;
    return () => {
        mountedShells -= 1;
        if (mountedShells === 0) pending = [];
    };
}

/** Called once the sonner Toaster has subscribed; sends everything that was waiting for it. */
export function toasterMounted(toast: Toast): void {
    loadedToast = toast;
    const waiting = pending;
    pending = [];
    for (const show of waiting) show(toast);
}
